import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../../services/api';
import './AdminCommon.css';

/**
 * WorkspaceSecretsPage -- list / create / rotate / delete workspace secrets.
 *
 * Secrets are write-only: the plaintext value is sent to the server during
 * creation or rotation and is never returned again. This page therefore
 * shows metadata only (name, description, timestamps) and offers a
 * "Rotate" button that re-prompts for the new value.
 */
function WorkspaceSecretsPage() {
  const [secrets, setSecrets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formValue, setFormValue] = useState('');
  const [formScope, setFormScope] = useState('workspace');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService.listWorkspaceSecrets()
      .then((data) => {
        if (cancelled) return;
        setSecrets(Array.isArray(data?.secrets) ? data.secrets : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || err?.message || 'Failed to load secrets');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const resetForm = () => {
    setFormName('');
    setFormDescription('');
    setFormValue('');
    setFormScope('workspace');
    setSubmitError(null);
    setEditingId(null);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openRotate = (secret) => {
    resetForm();
    setEditingId(secret.id);
    setFormName(secret.name);
    setFormDescription(secret.description || '');
    setShowForm(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (editingId) {
        const payload = { name: formName, description: formDescription || null };
        if (formValue) payload.value = formValue;
        await apiService.updateWorkspaceSecret(editingId, payload);
      } else {
        await apiService.createWorkspaceSecret({
          name: formName,
          description: formDescription || null,
          value: formValue,
          scope: formScope,
        });
      }
      setShowForm(false);
      resetForm();
      refresh();
    } catch (err) {
      setSubmitError(err?.response?.data?.error || err?.message || 'Failed to save secret');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (secret) => {
    if (!window.confirm(`Delete secret "${secret.name}"? This cannot be undone.`)) return;
    try {
      await apiService.deleteWorkspaceSecret(secret.id);
      refresh();
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Failed to delete secret');
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>Workspace Secrets</h1>
          <p className="admin-page-subtitle">
            Encrypted credential values used by HTTP and Python process-map nodes.
            Values are never returned to the UI after creation.
          </p>
        </div>
        <button className="admin-primary-btn" onClick={openCreate}>
          + New Secret
        </button>
      </div>

      {error && <div className="admin-error-banner">{error}</div>}
      {loading ? (
        <div className="admin-loading">Loading secrets…</div>
      ) : secrets.length === 0 ? (
        <div className="admin-empty">
          No secrets configured yet. Create one to wire up authenticated HTTP nodes
          or MCP servers.
        </div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Scope</th>
              <th>Description</th>
              <th>Key Version</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {secrets.map((s) => (
              <tr key={s.id}>
                <td><code>{s.name}</code></td>
                <td>{s.workspace_id ? 'workspace' : 'global'}</td>
                <td>{s.description || '—'}</td>
                <td>{s.key_version}</td>
                <td>{s.updated_at ? new Date(s.updated_at).toLocaleString() : '—'}</td>
                <td className="admin-table-actions">
                  <button className="admin-link-btn" onClick={() => openRotate(s)}>Rotate</button>
                  <button className="admin-link-btn danger" onClick={() => onDelete(s)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="admin-modal-backdrop" onClick={() => !submitting && setShowForm(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingId ? 'Rotate Secret' : 'New Workspace Secret'}</h2>
            <form onSubmit={onSubmit}>
              <div className="admin-form-row">
                <label>Name</label>
                <input
                  type="text"
                  required
                  disabled={!!editingId}
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. github_api_token"
                />
              </div>

              <div className="admin-form-row">
                <label>Description</label>
                <input
                  type="text"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Optional human-readable hint"
                />
              </div>

              <div className="admin-form-row">
                <label>
                  Value
                  {editingId && <span className="admin-form-hint"> (leave blank to keep current value)</span>}
                </label>
                <textarea
                  rows={3}
                  required={!editingId}
                  value={formValue}
                  onChange={(e) => setFormValue(e.target.value)}
                  placeholder={editingId ? 'New secret value (blank = no change)' : 'Secret value'}
                />
              </div>

              {!editingId && (
                <div className="admin-form-row">
                  <label>Scope</label>
                  <select value={formScope} onChange={(e) => setFormScope(e.target.value)}>
                    <option value="workspace">Workspace-scoped</option>
                    <option value="global">Global (all workspaces)</option>
                  </select>
                </div>
              )}

              {submitError && <div className="admin-error-banner">{submitError}</div>}

              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-secondary-btn"
                  disabled={submitting}
                  onClick={() => { setShowForm(false); resetForm(); }}
                >
                  Cancel
                </button>
                <button type="submit" className="admin-primary-btn" disabled={submitting}>
                  {submitting ? 'Saving…' : (editingId ? 'Save Changes' : 'Create Secret')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorkspaceSecretsPage;
